import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, UserPlus, HelpCircle, CheckCircle2, Clock, ExternalLink, X, AlertTriangle } from 'lucide-react';
import { Label } from '../ui/label';
import { Button } from '../ui/button';

// PM Requester picker — search existing PM users/contacts OR add an unlisted
// requester (pending contact) OR mark "Unknown / Not Provided". Requester is
// ALWAYS independent of Bill To. Every PM job ends with an intentional state:
//   verified | pending | external | unknown
const STATE_META = {
  verified: { label: 'Verified contact', cls: 'text-green-400 border-green-700/40', Icon: CheckCircle2 },
  pending: { label: 'Unlisted / pending review', cls: 'text-amber-400 border-amber-700/40', Icon: Clock },
  external: { label: 'External contact', cls: 'text-cyan-300 border-cyan-700/40', Icon: ExternalLink },
  unknown: { label: 'Unknown / not provided', cls: 'text-[#A3A3A3] border-[#525252]', Icon: HelpCircle },
};

const PmRequesterPicker = ({ API, companyId, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', phone: '', email: '', notes: '' });
  const [dupMatches, setDupMatches] = useState([]);
  const [liveMatches, setLiveMatches] = useState([]);
  const [saving, setSaving] = useState(false);
  const debRef = useRef(null);
  const addDebRef = useRef(null);

  const state = value?.state || '';
  const meta = STATE_META[state];

  const runSearch = useCallback((q) => {
    if (!companyId) return;
    setLoading(true);
    axios.get(`${API}/schedule/pm-company/${companyId}/requester-search`, { params: { q }, withCredentials: true })
      .then(({ data }) => setResults(data?.results || []))
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [API, companyId]);

  useEffect(() => {
    if (!open) return;
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(() => runSearch(query), 250);
    return () => debRef.current && clearTimeout(debRef.current);
  }, [query, open, runSearch]);

  // Live duplicate suggestions while typing an unlisted requester's name.
  useEffect(() => {
    if (!showAdd || !companyId) { setLiveMatches([]); return; }
    const nm = (addForm.name || '').trim();
    if (nm.length < 2) { setLiveMatches([]); return; }
    if (addDebRef.current) clearTimeout(addDebRef.current);
    addDebRef.current = setTimeout(() => {
      axios.get(`${API}/schedule/pm-company/${companyId}/requester-search`, { params: { q: nm }, withCredentials: true })
        .then(({ data }) => setLiveMatches((data?.results || []).slice(0, 4)))
        .catch(() => setLiveMatches([]));
    }, 300);
    return () => addDebRef.current && clearTimeout(addDebRef.current);
  }, [addForm.name, showAdd, companyId, API]);

  const pickResult = (r) => {
    if (r.kind === 'user') {
      onChange({ user_id: r.id, pending_id: '', name: r.name, state: 'verified' });
    } else {
      onChange({ user_id: '', pending_id: r.id, name: r.name, state: r.external ? 'external' : (r.verified ? 'verified' : 'pending') });
    }
    setOpen(false); setShowAdd(false); setDupMatches([]); setQuery('');
  };

  const setUnknown = () => {
    onChange({ user_id: '', pending_id: '', name: '', state: 'unknown' });
    setOpen(false); setShowAdd(false); setDupMatches([]);
  };

  const submitUnlisted = async (confirmed = false) => {
    const name = (addForm.name || '').trim();
    if (!name) { toast.error('Requester name is required'); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/schedule/pm-company/${companyId}/pending-contact`,
        { ...addForm, name, confirmed }, { withCredentials: true });
      if (!data.created && data.needs_confirm) {
        setDupMatches(data.possible_matches || []);
        toast.warning('Possible existing contact found — review below');
        return;
      }
      const c = data.contact;
      onChange({ user_id: '', pending_id: c.id, name: c.name, state: 'pending' });
      toast.success(`Unlisted requester added: ${c.name}`);
      setOpen(false); setShowAdd(false); setDupMatches([]);
      setAddForm({ name: '', phone: '', email: '', notes: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add requester');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-1" data-testid="pm-requester-picker">
      <Label className="text-[10px] uppercase tracking-wider text-cyan-300">Requester (who asked for the work) *</Label>

      {/* Current selection — wraps so the requester NAME stays readable even in
          the narrow shift sheet (badge no longer starves the name to 0 width). */}
      {!open && (
        <div className="flex items-center gap-2 flex-wrap bg-[#0A0A0A] border border-[#262626] rounded-md px-2.5 py-1.5 min-h-9">
          {state ? (
            <>
              <div className="flex items-center gap-2 min-w-0 basis-full">
                {meta?.Icon && <meta.Icon className={`w-3.5 h-3.5 shrink-0 ${meta.cls.split(' ')[0]}`} />}
                <span className="text-white text-sm truncate min-w-0" data-testid="pm-requester-current">
                  {state === 'unknown' ? 'Requester Unknown / Not Provided' : (value?.name || '—')}
                </span>
              </div>
              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border shrink-0 whitespace-nowrap ${meta?.cls}`}>{meta?.label}</span>
            </>
          ) : (
            <span className="text-[#737373] text-sm flex-1">No requester selected</span>
          )}
          <button type="button" data-testid="pm-requester-change" onClick={() => { setOpen(true); setResults([]); runSearch(''); }}
            className="text-[#D4AF37] text-xs font-bold hover:underline shrink-0">{state ? 'Change' : 'Select'}</button>
        </div>
      )}

      {/* Open picker */}
      {open && (
        <div className="bg-[#0A0A0A] border border-[#262626] rounded-md p-2 space-y-2">
          {!showAdd && (
            <>
              <div className="flex items-center gap-2 bg-[#141414] border border-[#262626] rounded px-2 h-9">
                <Search className="w-3.5 h-3.5 text-[#737373]" />
                <input
                  data-testid="pm-requester-search-input"
                  autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search name, phone, or email…"
                  className="bg-transparent text-white text-sm flex-1 outline-none"
                />
                <button type="button" onClick={() => setOpen(false)} className="text-[#737373] hover:text-white"><X className="w-4 h-4" /></button>
              </div>
              <div className="max-h-44 overflow-y-auto space-y-1" data-testid="pm-requester-results">
                {loading && <p className="text-[#737373] text-xs px-1">Searching…</p>}
                {!loading && results.length === 0 && <p className="text-[#737373] text-xs px-1">No matching contacts.</p>}
                {results.map((r) => (
                  <button
                    key={`${r.kind}-${r.id}`} type="button" data-testid={`pm-requester-opt-${r.id}`}
                    onClick={() => pickResult(r)}
                    className="w-full flex items-center gap-2 text-left bg-[#141414] hover:bg-[#1c1c1c] border border-[#262626] rounded px-2 py-1.5">
                    {r.verified ? <CheckCircle2 className="w-3.5 h-3.5 text-green-400 shrink-0" /> : <Clock className="w-3.5 h-3.5 text-amber-400 shrink-0" />}
                    <span className="text-white text-sm truncate flex-1">{r.name}
                      {r.email && <span className="text-[#737373]"> · {r.email}</span>}</span>
                    <span className="text-[9px] uppercase text-[#737373]">{r.kind === 'user' ? (r.role || '').replace(/_/g, ' ') : (r.verified ? 'contact' : 'pending')}</span>
                  </button>
                ))}
              </div>
              <div className="flex gap-2 pt-1">
                <Button type="button" size="sm" data-testid="pm-requester-add-unlisted"
                  onClick={() => { setShowAdd(true); setAddForm((f) => ({ ...f, name: query })); setDupMatches([]); }}
                  className="flex-1 bg-cyan-600 hover:bg-cyan-500 text-white h-8 text-xs"><UserPlus className="w-3.5 h-3.5 mr-1" /> Add Unlisted Requester</Button>
                <Button type="button" size="sm" variant="outline" data-testid="pm-requester-unknown"
                  onClick={setUnknown} className="flex-1 border-[#262626] text-[#A3A3A3] hover:bg-[#262626] h-8 text-xs"><HelpCircle className="w-3.5 h-3.5 mr-1" /> Unknown / Not Provided</Button>
              </div>
            </>
          )}

          {/* Add unlisted requester form */}
          {showAdd && (
            <div className="space-y-2" data-testid="pm-requester-add-form">
              <div className="flex items-center justify-between">
                <span className="text-cyan-300 text-xs font-bold uppercase tracking-wider">Add Unlisted Requester</span>
                <button type="button" onClick={() => { setShowAdd(false); setDupMatches([]); }} className="text-[#737373] hover:text-white"><X className="w-4 h-4" /></button>
              </div>
              <input data-testid="pm-requester-add-name" value={addForm.name} onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Name (required)" className="w-full bg-[#141414] border border-[#262626] rounded px-2 h-9 text-white text-sm outline-none" />
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="pm-requester-add-phone" value={addForm.phone} onChange={(e) => setAddForm((f) => ({ ...f, phone: e.target.value }))}
                  placeholder="Phone (optional)" className="bg-[#141414] border border-[#262626] rounded px-2 h-9 text-white text-sm outline-none" />
                <input data-testid="pm-requester-add-email" value={addForm.email} onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="Email (optional)" className="bg-[#141414] border border-[#262626] rounded px-2 h-9 text-white text-sm outline-none" />
              </div>
              <input data-testid="pm-requester-add-notes" value={addForm.notes} onChange={(e) => setAddForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Notes (optional)" className="w-full bg-[#141414] border border-[#262626] rounded px-2 h-9 text-white text-sm outline-none" />

              {liveMatches.length > 0 && dupMatches.length === 0 && (
                <div className="border border-cyan-700/40 bg-cyan-500/5 rounded p-2 space-y-1.5" data-testid="pm-requester-live-matches">
                  <p className="text-cyan-300 text-[11px]">Possible existing contact(s) — use one instead?</p>
                  {liveMatches.map((m) => (
                    <button key={`${m.kind}-${m.id}`} type="button" data-testid={`pm-requester-live-${m.id}`}
                      onClick={() => pickResult(m)}
                      className="w-full flex items-center gap-2 text-left bg-[#141414] hover:bg-[#1c1c1c] border border-[#262626] rounded px-2 py-1.5">
                      <span className="text-white text-sm truncate flex-1">{m.name}{m.email && <span className="text-[#737373]"> · {m.email}</span>}</span>
                      <span className="text-[10px] text-cyan-300 font-bold">Use this</span>
                    </button>
                  ))}
                </div>
              )}

              {dupMatches.length > 0 && (
                <div className="border border-amber-700/40 bg-amber-500/5 rounded p-2 space-y-1.5" data-testid="pm-requester-dup-warning">
                  <p className="text-amber-300 text-[11px] flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Possible existing contact(s) — use one, or continue creating.</p>
                  {dupMatches.map((m) => (
                    <button key={`${m.kind}-${m.id}`} type="button" data-testid={`pm-requester-dup-${m.id}`}
                      onClick={() => pickResult(m)}
                      className="w-full flex items-center gap-2 text-left bg-[#141414] hover:bg-[#1c1c1c] border border-[#262626] rounded px-2 py-1.5">
                      <span className="text-white text-sm truncate flex-1">{m.name}{m.email && <span className="text-[#737373]"> · {m.email}</span>}{m.phone && <span className="text-[#737373]"> · {m.phone}</span>}</span>
                      <span className="text-[10px] text-cyan-300 font-bold">Use this</span>
                    </button>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <Button type="button" size="sm" variant="outline" onClick={() => { setShowAdd(false); setDupMatches([]); }}
                  className="border-[#262626] text-[#A3A3A3] hover:bg-[#262626] h-8 text-xs">Back</Button>
                <Button type="button" size="sm" data-testid="pm-requester-add-save" disabled={saving}
                  onClick={() => submitUnlisted(dupMatches.length > 0)}
                  className="flex-1 bg-cyan-600 hover:bg-cyan-500 text-white h-8 text-xs">
                  {saving ? 'Saving…' : (dupMatches.length > 0 ? 'Create Anyway' : 'Add Requester')}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PmRequesterPicker;
